import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import DocumentUploadInput from "../../../components/shared/DocumentUploadInput";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { cn, getCharCount } from "../../../lib/utils.js";

const DocumentDialog = ({ isOpen, onOpenChange, selectedProject, onSuccess }) => {
  const api = useApi();
  const { user } = useAuth();
  const [newPhoto, setNewPhoto] = useState({
    projectId: "",
    title: "",
    description: "",
    location: "",
    uploadedBy: user?.id || ""
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const isDescriptionOverLimit = getCharCount(newPhoto.description) > 500;

  useEffect(() => {
    if (isOpen) {
      setNewPhoto((prev) => ({
        ...prev,
        uploadedBy: user?.id || "",
        projectId: selectedProject || ""
      }));
    }
  }, [isOpen, user?.id, selectedProject]);

  const uploadPhoto = async (e) => {
    e.preventDefault();

    if (isDescriptionOverLimit) {
      toast.error("Description exceeds the 500-character limit.");
      return;
    }

    if (photoFiles.length === 0) {
      toast.error("Please select at least one file to upload");
      return;
    }

    if (!newPhoto.projectId) {
      toast.error("Please select a project");
      return;
    }

    if (!newPhoto.title) {
      toast.error("Please enter a document title");
      return;
    }

    setIsUploading(true);
    try {
      const photosToUpload = photoFiles.map((data, index) => ({
        ...newPhoto,
        title: photoFiles.length > 1 ? `${newPhoto.title} - ${index + 1}` : newPhoto.title,
        photoData: data
      }));

      await api.post(`/photos/batch`, photosToUpload);

      if (onSuccess) onSuccess();
      resetForm();
      onOpenChange(false);
      toast.success(`Successfully uploaded ${photoFiles.length} file(s)!`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setNewPhoto({
      projectId: "",
      title: "",
      description: "",
      location: "",
      uploadedBy: user?.id || ""
    });
    setPhotoFiles([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>Upload Site Document</DialogTitle>
            <DialogDescription>Add new documents with location and timestamp</DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={uploadPhoto} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 space-y-6 py-6">
            <div>
              <Label htmlFor="projectId">
                Project <span className="text-red-500">*</span>
              </Label>
              <LazySearchSelect
                value={newPhoto.projectId}
                onChange={(val) => setNewPhoto({ ...newPhoto, projectId: val })}
                apiUrl="/projects"
                dataKey="items"
                labelKey="name"
                valueKey="id"
                placeholder="Select project"
                searchPlaceholder="Search projects..."
              />
            </div>

            <DocumentUploadInput
              value={photoFiles}
              onChange={(val) => setPhotoFiles(val || [])}
              label="Documents"
              required={true}
              multiple={true}
              helperText="Upload images, PDF, Word or Excel documents"
            />

            <div>
              <Label htmlFor="title">
                Document Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={newPhoto.title}
                onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })}
                placeholder="e.g., Foundation progress - Day 5"
                required
                className="mobile-text"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newPhoto.description}
                onChange={(e) => setNewPhoto({ ...newPhoto, description: e.target.value })}
                className={cn("mobile-text", isDescriptionOverLimit && "border-red-500")}
                rows={2}
                placeholder="Brief description of what's shown"
              />
              <WordCount text={newPhoto.description} />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={newPhoto.location}
                onChange={(e) => setNewPhoto({ ...newPhoto, location: e.target.value })}
                placeholder="e.g., North wing, Section A"
                className="mobile-text"
              />
            </div>
          </div>
          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button
              type="submit"
              variant="primary"
              disabled={isUploading || photoFiles.length === 0 || isDescriptionOverLimit}
              className="w-full py-6 rounded-lg font-bold shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Uploading {photoFiles.length} Documents...</span>
                </>
              ) : (
                <>
                  <span>{`Upload ${photoFiles.length > 0 ? `${photoFiles.length} ` : ""}Document${photoFiles.length > 1 ? "s" : ""}`}</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDialog;
